import { useState, useContext, useEffect } from 'react';
import { useSelector } from 'react-redux';

import { setItem } from 'app/services/storage.service';
import { QuotationService } from 'app/services/quotation.service';
import { CalculationDataByModelPathsInterface } from 'app/shared/interfaces/CalculationDataByModelPaths.interface';
import { CalculationDataByKeysInterface } from 'app/shared/interfaces/CalculationDataByKeys.interface';
import { RootState } from 'app/store/rootReducer';
import { selectSelectedOfferId } from 'app/store/data/selectedOffer';
import { getOffer } from 'app/store/data/quotationResult';
import { MubiInstallmentsContext } from 'app/components/MubiInstallmentsRadioOption/useMubiInstallments.context';
import { HiProPaymentService } from 'app/services/hiProPayment.services';
import { useNotification } from 'app/shared/hooks/useNotification';
import { useForm } from 'app/shared/hooks/useForm';
import { useConsent } from 'app/shared/hooks/useConsent';
import { selectQuotation } from 'app/store/data/quotation';
import { getQuestions } from 'app/store/data/formsConfig';
import {
  FormsEnum,
  LabelEnum,
  MessageTypeEnum,
  InstallmentsEnum,
} from 'app/shared/enums';
import {
  ErrorConfig,
  ErrorDecision,
  getMubiInstalmentError,
} from 'app/shared/helpers/getMubiInstalmentError';
import { ScrollService } from 'app/services/scroll.service';

export default function useApplicationInfo() {
  const [isLoading, setIsLoading] = useState(false);
  const notify = useNotification();

  const {
    setHiProToken,
    closeModal,
    setHiProConsents,
    setErrorOnModal,
    mubiInstallmentsDataLayerEvent,
    setActiveStep,
  } = useContext(MubiInstallmentsContext);

  useEffect(() => {
    mubiInstallmentsDataLayerEvent(
      InstallmentsEnum.EventModalStep,
      InstallmentsEnum.EventStepNameEntry
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const {
    consentChecked,
    consentError,
    handleConsentClick,
    setConsentError,
  } = useConsent();

  const {
    valuesForRegisteredFieldsOnly: formValues,
  } = useForm<CalculationDataByKeysInterface>(FormsEnum.CalculationForm);

  const offerId = useSelector(selectSelectedOfferId);
  const offer = useSelector(getOffer(offerId));
  const quotation = useSelector(selectQuotation);
  const questions = useSelector((state: RootState) =>
    getQuestions(state, FormsEnum.CalculationForm)
  );

  const handleActionButtonClick = async () => {
    if (!consentChecked) {
      setConsentError(true);
    } else {
      setIsLoading(true);
      await registerHiProPayment();
      setIsLoading(false);
    }
  };

  const registerHiProPayment = async () => {
    if (!offer) {
      return;
    }

    const data = QuotationService.getValuesByModelPaths<CalculationDataByModelPathsInterface>(
      formValues,
      questions,
      quotation
    );

    if (data.payment.numberOfInstallments === 'hipro') {
      data.payment.numberOfInstallments = 1;
    }

    mubiInstallmentsDataLayerEvent(
      InstallmentsEnum.EventNameBeginApplication,
      InstallmentsEnum.EventStepNameEntry
    );

    await HiProPaymentService.registerHiProPayment({
      resultId: offer.resultId,
      offerId: offer.id,
      quotation: data,
      hiProConsentGranted: consentChecked,
    })
      .then(({ data }) => {
        setHiProToken(data.token);
        setItem(`hiPro_token_offer_${offer.id}`, { token: data.token });
        setHiProConsents(data.consents);
        setActiveStep(1);
      })
      .catch(({ response }) => {
        const hiProError =
          response?.data &&
          getMubiInstalmentError(
            response.data.errorKey || response.data.message
          );

        if (!hiProError) {
          notify(MessageTypeEnum.Danger, LabelEnum.ServerError);
        } else if (hiProError === ErrorDecision.NegativeDecision) {
          setActiveStep(7);
        } else {
          closeModal();
          setErrorOnModal(hiProError as ErrorConfig);
        }
      });
  };

  const handleHiproConsentClick = () => {
    handleConsentClick();

    const element = document.getElementById('hipro-btn-start-process');
    const scroller = document.getElementById('mubi-installments-scrollbars')
      ?.firstElementChild;

    if (element) {
      ScrollService.scrollToElement(element, { scroller });
    }
  };

  return {
    handleHiproConsentClick,
    handleActionButtonClick,
    isLoading,
    hiProConsentError: consentError,
    hiProConsentChecked: consentChecked,
  };
}
